import React, { useEffect, useState } from 'react';
import { Alert, Button, Input, Modal, Space, Spin, Typography } from 'antd';
import { useTranslation } from '../../hooks/useTranslation';
import { getId } from '../../helpers/file';
import { getExpireTimeDisplay } from '../../utils/storageTypeUtils';
import {
  getPermanentSave, getPermanentSaveQuota, PermanentSaveQuota, PermanentSaveRequest, PermanentSaveSummary,
  permanentSaveStatusKey, submitPermanentSave,
} from '../../services/permanentSave';

const VIP_LEVELS = ['EXPERIENCE', 'WEEK', 'MONTH', 'TWO_MONTH', 'YEAR'] as const;
type VipLevel = typeof VIP_LEVELS[number];

interface Props {
  open: boolean;
  uploadRecordId: number;
  fileName: string;
  storageType?: string;
  expireTime?: string;
  application?: PermanentSaveSummary;
  onClose: () => void;
  onSubmitted: (request: PermanentSaveRequest) => void;
}

const PermanentSaveModal: React.FC<Props> = ({
  open, uploadRecordId, fileName, storageType, expireTime, application, onClose, onSubmitted,
}) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [detail, setDetail] = useState<PermanentSaveRequest>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [reload, setReload] = useState(0);
  const [quota, setQuota] = useState<PermanentSaveQuota>();
  const requestId = application?.id;

  useEffect(() => { if (open) setSubmitted(false); }, [open]);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setError('');
    setReason('');
    setDetail(undefined);
    setQuota(undefined);
    getPermanentSaveQuota().then(result => {
      if (active) setQuota(result);
    }).catch(() => {
      // The submit endpoint remains authoritative if the informational request fails.
    });
    if (requestId === undefined) { setLoading(false); return; }
    setLoading(true);
    getPermanentSave(requestId).then(result => {
      if (!active) return;
      setDetail(result);
      setReason(result.reason);
    }).catch((failure: Error) => {
      if (active) setError(failure.message);
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, requestId, reload]);

  const current = detail || application;
  const editable = !current || current.reviewStatus === 'REJECTED';
  const quotaExhausted = editable && quota !== undefined && quota.remaining <= 0;
  const length = Array.from(reason.trim()).length;
  const vipLevelLabel = (level: VipLevel) => t(`permanentSave.vipLevels.${level}`);
  const submit = async () => {
    if (!editable || quotaExhausted || length < 1 || length > 500 || saving) return;
    setSaving(true);
    setError('');
    try {
      const result = await submitPermanentSave(uploadRecordId, getId() || '', reason.trim());
      setDetail(result);
      setSubmitted(true);
      onSubmitted(result);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'permanentSave.requestFailed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={t('permanentSave.title')} open={open} onCancel={saving ? undefined : onClose}
      width={540} maskClosable={!saving} closable={!saving}
      footer={[
        <Button key="close" onClick={onClose} disabled={saving}>{t('common.close')}</Button>,
        current && <Button key="refresh" onClick={() => setReload(value => value + 1)} disabled={saving || loading}>
          {t('permanentSave.refresh')}
        </Button>,
        editable && <Button key="submit" type="primary" onClick={submit} loading={saving}
          disabled={loading || quotaExhausted || length < 1 || length > 500 || Boolean(error && requestId && !detail)}>
          {t('permanentSave.submit')}
        </Button>,
      ].filter(Boolean)}>
      <Spin spinning={loading}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Typography.Text strong style={{ overflowWrap: 'anywhere' }}>{fileName}</Typography.Text>
          <Typography.Text type="secondary">
            {t('permanentSave.retention')}{getExpireTimeDisplay(storageType, expireTime)}
          </Typography.Text>
          {quota && <Alert type={quotaExhausted ? 'warning' : 'info'} showIcon
            message={t('permanentSave.quota', quota)} />}
          {quota && <Alert type="info" showIcon
            message={t('permanentSave.quotaRuleTitle')}
            description={(
              <Space direction="vertical" size={4}>
                <Typography.Text>
                  {t('permanentSave.quotaRuleBase', { baseLimit: quota.baseLimit })}
                </Typography.Text>
                <Typography.Text>
                  {quota.activeVip
                    ? (quota.vipLevel && VIP_LEVELS.includes(quota.vipLevel as VipLevel)
                      ? t('permanentSave.quotaRuleCurrentVip', {
                        level: vipLevelLabel(quota.vipLevel as VipLevel), bonus: quota.vipBonus,
                      })
                      : t('permanentSave.quotaRuleUnknownVip'))
                    : t('permanentSave.quotaRuleNonVip')}
                </Typography.Text>
                <Typography.Text type="secondary">{t('permanentSave.quotaRuleList')}</Typography.Text>
                {VIP_LEVELS.map(level => (
                  <Typography.Text type="secondary" key={level}>
                    {t('permanentSave.quotaRuleItem', {
                      level: vipLevelLabel(level), bonus: quota.vipBonuses?.[level] ?? 0,
                    })}
                  </Typography.Text>
                ))}
              </Space>
            )}
          />}
          <Alert type="info" showIcon message={t('permanentSave.reviewHint')}
            description={t('permanentSave.pendingHint')} />
          <Typography.Text strong>
            {t('permanentSave.wechatHint')}{' '}
            <Typography.Text copyable={{ text: '18215600321' }}>18215600321</Typography.Text>
          </Typography.Text>
          {current && <Alert showIcon type={current.reviewStatus === 'REJECTED' || current.executionStatus === 'FAILED' ? 'warning' : 'info'}
            message={t(permanentSaveStatusKey(current))}
            description={current.reviewReason === 'permanentSave.deleted' ? t(current.reviewReason) : current.reviewReason} />}
          {submitted && <Alert type="success" showIcon message={t('permanentSave.submitted')} />}
          {error && <Alert type="error" showIcon message={t(error)} />}
          {quotaExhausted && <Alert type="warning" showIcon message={t('permanentSave.quotaExceeded')} />}
          <label htmlFor={`permanent-save-reason-${uploadRecordId}`}>{t('permanentSave.reason')}</label>
          <Input.TextArea id={`permanent-save-reason-${uploadRecordId}`} value={reason}
            onChange={event => setReason(event.target.value)} disabled={!editable || loading || saving}
            autoSize={{ minRows: 4, maxRows: 8 }} placeholder={t('permanentSave.reasonPlaceholder')} />
          <Typography.Text type={length > 500 ? 'danger' : 'secondary'}>{length}/500</Typography.Text>
        </Space>
      </Spin>
    </Modal>
  );
};

export default PermanentSaveModal;
